'use client';

import type { ReactElement } from 'react';
import { BlockErrorBoundary } from './BlockErrorBoundary';
import { useBlockResolver, useLocale, useThemeId } from './contexts';
import { localizeTree } from './localize';
import type { BlockInstance } from './types';

/**
 * Hook returning a closure that renders an array of {@link BlockInstance}
 * children. Used by container blocks ({@code Section}, {@code Columns},
 * etc.) to render their nested content through the same resolver +
 * locale pipeline the top-level {@code BlockRenderer} uses.
 *
 * <p>Why a hook (not a plain function): {@code useThemeId} and
 * {@code useLocale} can only be read inside a component. The hook
 * returns a closure so container blocks call it once and re-use the
 * renderer across multiple slots (Columns has col1..col4 etc.).
 *
 * <p>Port of ThemeMaker's {@code useChildrenRenderer} in
 * {@code components/block-system/renderChildren.tsx} — same algorithm,
 * adapted to the context-based resolver.
 */
export function useChildrenRenderer(): (items: unknown[]) => ReactElement[] {
  const resolver = useBlockResolver();
  const themeId = useThemeId();
  const { locale, fallback } = useLocale();

  return function renderChildren(items: unknown[]): ReactElement[] {
    if (!Array.isArray(items)) return [];
    const out: ReactElement[] = [];
    items.forEach((it, i) => {
      if (!it || typeof it !== 'object') return;
      const instance = it as BlockInstance;
      if (typeof instance.type !== 'string') return;
      const Component = resolver.resolveBlock(themeId, instance.type);
      if (!Component) {
        out.push(
          <div
            key={i}
            style={{
              margin: 8,
              padding: 8,
              border: '1px dashed var(--color-neutral-200, #e5e7eb)',
              borderRadius: 4,
              fontSize: 12,
              color: 'var(--color-text-secondary, #6b7280)',
            }}
          >
            Unknown block: {instance.type}
          </div>,
        );
        return;
      }
      const localizedProps = localizeTree(instance.props ?? {}, locale, fallback) as Record<
        string,
        unknown
      >;
      out.push(
        <BlockErrorBoundary key={i} blockType={instance.type}>
          <Component {...localizedProps} />
        </BlockErrorBoundary>,
      );
    });
    return out;
  };
}
